import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';

/**
 * `contents[]` keys that belong to their own designed block (contract v1.12 §5) rather than the
 * generic accordion.
 *
 * `title` and `description` are folded into content blocks on ingestion (`EventMapper`'s
 * `titleI18n`/`descriptionI18n` handling) and are consumed for the DTO's own `title`/`description`
 * — they still ship inside `contents[]`, so without holding them back the detail page renders
 * duplicate "TITLE"/"DESCRIPTION" accordions on top of the hero and the body copy.
 *
 * The Host block itself is not built yet, so those three are held back; `event_info` renders as
 * body copy below the description.
 */
export const STRUCTURED_CONTENT_KEYS = [
  'title',
  'description',
  'host_name',
  'host_bio',
  'host_image',
  'event_info',
];

/**
 * Content keys with a designed label. The server treats the key set as extensible
 * (`EventContentDto.ContentKey`: "description, schedule, dress_code, …"), so this is a
 * best-effort map, never an exhaustive one — unmapped keys fall back to {@link prettifyContentKey}.
 */
const CONTENT_LABEL_KEYS: Record<string, TranslationKey> = {
  dress_code: 'App.Events.ContentKey.DressCode',
  schedule: 'App.Events.ContentKey.Schedule',
};

/**
 * Raw key as a readable label — `dress_code` becomes `dress code`. The accordion trigger already
 * uppercases through CSS, so the casing is deliberately left alone here.
 */
export function prettifyContentKey(key: string): string {
  return key.replace(/_/g, ' ').trim();
}

/**
 * Resolves a raw `contentKey` to its accordion label: a known key returns its translation, an
 * unknown one returns the prettified key. Falling back rather than hiding matches how unknown
 * `eventCategory` values are handled (see `useEventCategoryLabel`) — Salesforce can add a key at
 * any time and the content behind it must still reach the member.
 */
export function useEventContentLabel(): (key?: string | null) => string | undefined {
  const t = useClientTranslation();
  return (key?: string | null) => {
    if (!key?.trim()) return undefined;
    const translationKey = CONTENT_LABEL_KEYS[key.trim().toLowerCase()];
    return translationKey ? t(translationKey) : prettifyContentKey(key);
  };
}
